import { Component, inject, input, output, signal, computed, effect, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { RequestService, Request } from '../../services/requests.service';
import { BpmService } from '../../services/bpm.service';
import { DocumentApprovalComponent } from '../../../management-document/components/document-approval/document-approval.component';
import { DocumentService } from '../../../management-document/services/document.service';
import { PartyFormComponent } from '../../../management-parties/components/party-form/party-form.component';
import { VehicleFormComponent } from '../../../management-vehicle/components/vehicle-form/vehicle-form.component';
import { FormMode } from 'app/core/constants/form-mode';
import { TimelineComponent, TimelineStep } from 'app/core/components/timeline/timeline.component';
import { FlowBadgeComponent } from 'app/core/components/flow-badge/flow-badge.component';
import { FlowStepsComponent } from 'app/core/components/flow-steps/flow-steps.component';
import { MembershipInformePdfComponent } from '../membership-informe-pdf/membership-informe-pdf.component';
import { StepApprovalPayload } from '../step-approval-modal/step-approval-modal.component';
import { BpmComponent } from '../bpm/bpm.component';
import { AuthService } from '@services/core/auth.service';
import { AppSettings } from '@services/settings/app-settings.service';
import { MobileFooterComponent } from 'app/core/components/mobile-footer/mobile-footer.component';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-request-party',
  standalone: true,
  imports: [
    CommonModule, ReactiveFormsModule,
    DocumentApprovalComponent, PartyFormComponent, VehicleFormComponent,
    TimelineComponent, FlowBadgeComponent, FlowStepsComponent, MembershipInformePdfComponent,
    BpmComponent, MobileFooterComponent
  ],
  templateUrl: './request-party.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./request-party.component.scss']
})
export class RequestPartyComponent {
  request = input.required<Request>();
  reviewCompleted = output<boolean>();

  @ViewChild('membershipPdf') membershipPdf!: MembershipInformePdfComponent;
  @ViewChild('documentApproval') documentApproval?: DocumentApprovalComponent;
  @ViewChild(BpmComponent) bpmActions!: BpmComponent;

  private requestService = inject(RequestService);
  private bpmService = inject(BpmService);
  private documentService = inject(DocumentService);
  private fb = inject(FormBuilder);
  private authService = inject(AuthService);
  public appSettings = inject(AppSettings);
  private Toast = Swal.mixin({
    toast: true,
    position: 'top-end',
    showConfirmButton: false,
    timer: 3000,
    timerProgressBar: true
  });

  public partyForm!: FormGroup;
  public vehicleForm!: FormGroup;
  public formMode = FormMode;

  selectedRequest = signal<Request | null>(null);
  currentStep = signal<number>(1);
  isProcessingApproval = signal<boolean>(false);
  hasObservedDocs = signal<boolean>(false);
  /** Documentos requeridos de incorporación con archivo (vía document-approval). */
  allRequiredDocsUploaded = signal<boolean>(false);

  // Abre la previsualizacion oficial del PDF de incorporacion
  openPdfInforme(): void {
    this.membershipPdf?.openPreview();
  }

  private role = computed(() => {
    const roleObj = this.authService.sharingRole() as any;
    return (roleObj?.slug ?? roleObj?.name ?? 'VISUALIZADOR').toUpperCase();
  });

  private userMail = computed(() => {
    const identity = this.authService.sharingIdentity() as any;
    const data = this.authService.userData() as any;
    return String(identity?.mail ?? data?.mail ?? '').trim().toLowerCase() || null;
  });

  timelineSteps = computed<TimelineStep[]>(() => {
    const request = this.selectedRequest();
    if (!request) return [];

    const steps: TimelineStep[] = [{ id: 1, title: 'Socio', icon: 'fas fa-user' }];
    let currentId = 2;
    if (
      request.vehicle
      || request.processSlug === 'VEHICLE_NEW'
      || this.bpmService.isVehicleUpdateProcess(request.processSlug)
    ) {
      steps.push({ id: currentId++, title: 'Vehículo', icon: 'fas fa-car' });
    }
    if (this.hasDocumentChecklist()) {
      steps.push({ id: currentId++, title: 'Documentos', icon: 'fas fa-folder-open' });
    }
    steps.push({ id: currentId++, title: 'Aprobación', icon: 'fas fa-gavel' });
    return steps;
  });

  /** Checklist de requisitos (no el doc aprobatorio BPM). */
  hasDocumentChecklist = computed(() => {
    const request = this.selectedRequest() as any;
    if (!request) return false;
    const snap = request.documentRequirementsSnapshot ?? request.document_requirements_snapshot;
    const items = Array.isArray(snap?.items) ? snap.items : null;
    if (items) return items.length > 0;
    // Sin snapshot aún: asumir sí para procesos de membresía/vehículo (compat)
    const slug = String(request.processSlug || '');
    return slug === 'MEMBERSHIP_NEW' || slug === 'VEHICLE_NEW' || slug === 'DOC_RENEWAL';
  });

  currentStepObj = computed(() => {
    const steps = this.timelineSteps();
    const currentId = this.currentStep();
    return steps.find(s => s.id === currentId) ?? null;
  });

  canApprove = computed(() => this.bpmService.canApprove(this.selectedRequest(), this.role()));
  canReviewDocuments = computed(() => this.bpmService.canReviewContent(this.selectedRequest(), this.role()));
  canFinalApproveDocs = computed(() => this.bpmService.canFinalContentReview(this.selectedRequest(), this.role()));
  canObserveDocs = computed(() => this.bpmService.canObserve(this.selectedRequest(), this.role()));
  nextStatusSlug = computed(() => this.bpmService.nextStatusSlug(this.selectedRequest()));
  hasVehicleStep = computed(() => this.timelineSteps().some(s => s.title === 'Vehículo'));

  isVehicleUpdate = computed(() =>
    this.bpmService.isVehicleUpdateProcess(this.selectedRequest()?.processSlug)
  );

  proposedChangeRows = computed(() => {
    const raw = (this.selectedRequest() as any)?.proposedChanges
      ?? (this.selectedRequest() as any)?.proposed_changes;
    if (!raw || typeof raw !== 'object') return [];
    const labels: Record<string, string> = {
      plate: 'Patente',
      year: 'Año',
      brandCode: 'Marca',
      modelCode: 'Modelo',
      vin: 'N° motor / VIN',
      value: 'Valor compra',
      commercialValue: 'Valor comercial',
      color: 'Color',
      km: 'Kilometraje'
    };
    return Object.entries(raw).map(([key, val]: [string, any]) => ({
      key,
      label: labels[key] || key,
      from: val?.from ?? '—',
      to: val?.to ?? '—'
    }));
  });

  canReplaceObserved = computed(() =>
    this.bpmService.canReplaceObservedDocuments(this.selectedRequest(), this.role(), this.userMail())
  );

  isObserved = computed(() => this.bpmService.isObserved(this.selectedRequest()));

  canResubmit = computed(() =>
    this.bpmService.canResubmitObservation(this.selectedRequest(), this.role(), this.userMail())
  );

  observationReason = computed(() => this.bpmService.observationReason(this.selectedRequest()));

  isDraft = computed(() => this.bpmService.isDraft(this.selectedRequest()));

  canSubmitDraft = computed(() => {
    if (!this.bpmService.canSubmitDraft(this.selectedRequest())) return false;
    if (!this.bpmService.canInitiateProcess(this.selectedRequest()?.processSlug ?? '', this.role())) {
      return false;
    }
    if (!this.hasDocumentChecklist()) return true;
    return this.allRequiredDocsUploaded();
  });

  canActApproval = computed(() => this.canSubmitDraft() || this.canApprove());

  showRejectMobile = computed(() => this.canApprove() && !this.isDraft());

  showApprovalMobileFooter = computed(() =>
    this.appSettings.isMobile()
    && this.currentStepObj()?.title === 'Aprobación'
    && (this.canActApproval() || this.isDraft())
  );

  canConfirmApproval = computed(() =>
    this.canActApproval() && !this.isProcessingApproval() && !this.bpmBlockedReason()
  );

  approveMobileLabel = computed(() => {
    if (this.isDraft()) return 'Enviar solicitud';
    const next = this.nextStatusSlug();
    if (next === 'REIMBURSED') return 'Reembolso';
    if (this.bpmService.isPaymentStep(this.selectedRequest())) return 'Pago';
    return 'Aprobar';
  });

  onMobileApprove(): void {
    this.bpmActions?.onApproveClick();
  }

  onMobileReject(): void {
    this.bpmActions?.onRejectClick();
  }

  onDocumentsReadiness(ready: boolean): void {
    this.allRequiredDocsUploaded.set(ready);
  }

  bpmBlockedReason = computed(() => {
    // Origen corrigiendo / OBSERVED por sync de requisitos
    if (this.canResubmit() || this.canReplaceObserved()) {
      if (this.hasObservedDocs()) {
        return 'Reemplace los documentos observados antes de reenviar.';
      }
      if (this.hasDocumentChecklist() && !this.allRequiredDocsUploaded()) {
        return 'Suba los requisitos documentales pendientes antes de reenviar.';
      }
      return null;
    }
    if (this.hasObservedDocs() || this.isObserved()) {
      return 'Hay documentos observados. Espere la corrección antes de avanzar.';
    }
    if (this.isDraft() && !this.allRequiredDocsUploaded()) {
      return 'Debe cargar todos los documentos requeridos antes de enviar.';
    }
    return null;
  });

  constructor() {
    this.createForm();

    effect(() => {
      const req = this.request();
      const prevUuid = this.selectedRequest()?.uuid;
      this.selectedRequest.set(req);

      if (req.party) {
        this.partyForm.patchValue(req.party);
      } else {
        this.partyForm.reset();
      }

      if (req.vehicle) {
        this.vehicleForm.patchValue({
          ...req.vehicle,
          commercialValue: this.formatDisplayNumber(req.vehicle.commercialValue),
          value: this.formatDisplayNumber(req.vehicle.value)
        });
      } else {
        this.vehicleForm.reset();
      }

      if (req.uuid !== prevUuid) {
        this.currentStep.set(this.landingStepFor(req));
        this.allRequiredDocsUploaded.set(false);
        this.hasObservedDocs.set(false);
      }
    }, { allowSignalWrites: true });
  }

  // Formatea un número agregando separadores de miles para visualización
  private formatDisplayNumber(value: unknown): string {
    if (value === null || value === undefined || value === '') return '';
    const clean = String(value).replace(/\./g, '').replace(',', '.').trim();
    const num = Number(clean);
    return isNaN(num) ? '' : num.toLocaleString('de-DE');
  }

  loadRequests() {
    this.requestService.loadRequests();
  }

  onReviewCompleted() {
    this.reviewCompleted.emit(false);
  }

  async resubmitObservation(): Promise<void> {
    const req = this.selectedRequest();
    if (!req || !this.canResubmit()) return;
    if (this.hasObservedDocs()) {
      await Swal.fire({
        icon: 'warning',
        title: 'Corrección pendiente',
        text: 'Reemplace todos los documentos observados antes de reenviar.'
      });
      return;
    }
    if (this.hasDocumentChecklist() && !this.allRequiredDocsUploaded()) {
      await Swal.fire({
        icon: 'warning',
        title: 'Documentos pendientes',
        text: 'Suba todos los requisitos documentales nuevos antes de reenviar.'
      });
      return;
    }
    const target = this.bpmService.resubmitTargetSlug(req);
    if (!target) {
      await Swal.fire('Atención', 'No hay destino configurado para subsanar.', 'info');
      return;
    }
    const confirm = await Swal.fire({
      title: 'Reenviar corrección',
      text: '¿Enviar de nuevo a revisión con los archivos actualizados?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Sí, reenviar'
    });
    if (!confirm.isConfirmed) return;
    this.isProcessingApproval.set(true);
    try {
      await firstValueFrom(this.requestService.advanceFlowStep(req.uuid, {
        toStatusSlug: target,
        comments: 'Documentos corregidos / reenviados'
      }));
      this.finishAdvance(true);
    } catch (err: any) {
      const msg = err?.error?.error ?? err?.error?.message ?? err?.message ?? 'Intente nuevamente.';
      await Swal.fire({ icon: 'error', title: 'No se pudo reenviar', text: msg });
    } finally {
      this.isProcessingApproval.set(false);
    }
  }

  private finishAdvance(keepOpen: boolean): void {
    if (keepOpen) this.goToApproval();
    this.reviewCompleted.emit(keepOpen);
  }

  private goToApproval(): void {
    const steps = this.timelineSteps();
    const id = steps.find(s => s.title === 'Aprobación')?.id ?? steps[steps.length - 1]?.id ?? 1;
    this.currentStep.set(id);
  }

  private landingStepFor(req: Request): number {
    if (['APPROVED', 'PENDING_PAYMENT', 'PAID', 'REJECTED'].includes(req.statusSlug || '')) {
      const steps = this.timelineSteps();
      return steps.find(s => s.title === 'Aprobación')?.id ?? steps.length;
    }
    if (this.bpmService.isObserved(req) || this.canReplaceObserved()) {
      const steps = this.timelineSteps();
      return steps.find(s => s.title === 'Documentos')?.id ?? 1;
    }
    return 1;
  }

  async approveRequest(): Promise<void> {
    const req = this.selectedRequest();
    if (!req || this.isProcessingApproval()) return;
    if (this.bpmBlockedReason()) {
      await Swal.fire({ icon: 'warning', title: 'Acción bloqueada', text: this.bpmBlockedReason()! });
      return;
    }
    if (this.bpmService.isDraft(req)) {
      if (!this.allRequiredDocsUploaded()) {
        await Swal.fire({
          icon: 'warning',
          title: 'Documentos pendientes',
          text: 'Debe cargar todos los documentos requeridos antes de enviar.'
        });
        return;
      }
      const res = await Swal.fire({
        title: 'Enviar solicitud',
        text: '¿Enviar la solicitud al flujo de aprobación?',
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Sí, enviar'
      });
      if (res.isConfirmed) await this.executeApproval();
      return;
    }
    if (this.bpmService.stepRequiresDocument(req) || this.bpmService.isPaymentStep(req)) {
      this.bpmActions?.openModal();
      return;
    }
    const res = await Swal.fire({
      title: 'Aprobar Solicitud',
      text: '¿Confirmar la aprobación y avanzar al siguiente paso?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Sí, aprobar'
    });
    if (res.isConfirmed) await this.executeApproval();
  }

  async onStepApprovalConfirmed(payload: StepApprovalPayload): Promise<void> {
    if (!this.selectedRequest()) return;
    this.isProcessingApproval.set(true);
    try {
      await this.sendApprovalRequest(payload);
    } catch (err: any) {
      await this.handleApprovalError(err);
    } finally {
      this.isProcessingApproval.set(false);
    }
  }

  private async sendApprovalRequest(payload: StepApprovalPayload): Promise<void> {
    const req = this.selectedRequest()!;
    const nextSlug = this.nextStatusSlug();
    await firstValueFrom(this.requestService.advanceFlowStep(req.uuid, {
      toStatusSlug: nextSlug,
      gloss: payload.gloss,
      documentUuid: payload.documentUuid,
      documentTypeName: payload.documentTypeName,
      requiresDocument: payload.requiresDocument
    }));
    this.bpmActions?.closeModal();
    this.finishAdvance(this.bpmService.shouldStayOpenAfterAdvance(req, nextSlug, this.role()));
  }

  private async executeApproval(): Promise<void> {
    const req = this.selectedRequest();
    if (!req) return;
    this.isProcessingApproval.set(true);
    try {
      await this.sendBasicApproval(req);
    } catch (err: any) {
      await this.handleApprovalError(err);
    } finally {
      this.isProcessingApproval.set(false);
    }
  }

  private async sendBasicApproval(req: Request): Promise<void> {
    const nextSlug = this.bpmService.isDraft(req)
      ? this.bpmService.submitTargetStatusSlug(req)
      : this.nextStatusSlug();
    await firstValueFrom(this.requestService.advanceFlowStep(req.uuid, {
      toStatusSlug: nextSlug
    }));
    this.finishAdvance(this.bpmService.shouldStayOpenAfterAdvance(req, nextSlug, this.role()));
  }

  private async handleApprovalError(err: any): Promise<void> {
    const msg = err?.error?.error ?? err?.error?.message ?? err?.message ?? 'Intente nuevamente.';
    await Swal.fire({ icon: 'error', title: 'Error al aprobar', text: msg });
  }

  async rejectRequest() {
    if (!this.selectedRequest()) return;
    const { value: reason } = await Swal.fire({
      title: 'Rechazar Solicitud',
      input: 'textarea',
      inputLabel: 'Motivo del Rechazo (Obligatorio)',
      inputPlaceholder: 'Escriba por qué se rechaza...',
      inputValidator: (v) => !v ? 'El motivo es obligatorio para rechazar' : null,
      showCancelButton: true,
      confirmButtonText: 'Confirmar Rechazo',
      confirmButtonColor: '#d33'
    });
    if (!reason) return;
    this.requestService.advanceFlowStep(this.selectedRequest()!.uuid, {
      toStatusSlug: 'REJECTED',
      reason
    }).subscribe(() => this.onReviewCompleted());
  }

  nextStep(): void {
    if (this.currentStep() < this.timelineSteps().length) {
      this.currentStep.update(step => step + 1);
    }
  }

  prevStep(): void {
    if (this.currentStep() > 1) {
      this.currentStep.update(step => step - 1);
    }
  }

  // Reemplaza un documento observado en segundo plano y refresca
  onUploadDocumentFile(event: { uuid: string; file: File; origin?: string; done?: (ok: boolean, res?: any) => void }): void {
    const origin = (event.origin ?? 'PARTY') as any;
    this.documentService.uploadDocument(event.uuid, event.file, origin).subscribe({
      next: (res) => this.handleUploadSuccess(event, res),
      error: (e) => this.handleUploadError(event, e)
    });
  }

  // Notifica éxito y actualiza listados tras reemplazo
  private handleUploadSuccess(event: any, res: any): void {
    event.done?.(true, res);
    this.Toast.fire({ icon: 'success', title: 'Archivo reemplazado con éxito' });
    this.loadRequests();
  }

  // Notifica error al fallar la subida
  private handleUploadError(event: any, e: any): void {
    event.done?.(false);
    this.Toast.fire({ icon: 'error', title: e?.error?.message ?? 'No se pudo subir el archivo.' });
  }

  private createForm() {
    this.partyForm = this.fb.group({
      uuid: [null],
      rut: [null],
      partyTypeCode: [null],
      names: [null],
      birthday: [null],
      mail: [null],
      mobileNumber: [null],
      groupCode: [null],
      entityCode: [null],
    });

    this.vehicleForm = this.fb.group({
      uuid: [null],
      plate: [null],
      brandCode: [null],
      color: [null],
      modelCode: [null],
      year: [null],
      vin: [null],
      km: [null],
      vehicleType: [null],
      commercialValue: [null],
      value: [null],
      statusCode: [null],
      isTransmissionAutomatic: [null],
      entityCode: [null]
    });
  }
}